import { useImpersonationStore } from '../stores/impersonation.store';
import { getTabAuthAs } from '../tab-auth-as';

/**
 * Single source of truth for impersonation.
 *
 * `impersonatedUser` is the persisted selection. `tabAuthAsUserId` is the
 * session-only deep-link override. `authAsUserId` is the id the app should
 * act as: the tab override when present, otherwise the persisted user.
 *
 * Call `captureTabAuthAsFromSearch` before this hook on the render that may
 * introduce a new `?auth_as=` param, so the session value is already stored.
 */
export function useImpersonation() {
  const impersonatedUser = useImpersonationStore((state) => state.impersonatedUser);
  const recentUsers = useImpersonationStore((state) => state.recentUsers);
  const setImpersonatedUser = useImpersonationStore((state) => state.setImpersonatedUser);
  const addRecentUser = useImpersonationStore((state) => state.addRecentUser);
  const removeRecentUser = useImpersonationStore((state) => state.removeRecentUser);
  const clearImpersonatedUser = useImpersonationStore((state) => state.clearImpersonatedUser);
  const clearAll = useImpersonationStore((state) => state.clearAll);

  const tabAuthAsUserId = getTabAuthAs();
  const authAsUserId = tabAuthAsUserId ?? impersonatedUser?.id;

  return {
    impersonatedUser,
    recentUsers,
    setImpersonatedUser,
    addRecentUser,
    removeRecentUser,
    clearImpersonatedUser,
    clearAll,
    /** Tab-scoped deep-link user id, or null when this tab has no override. */
    tabAuthAsUserId,
    /** Effective auth_as id: tab override, then the persisted impersonated user. */
    authAsUserId,
    /** True when this tab is impersonating via the session override. */
    isTabAuthAs: !!tabAuthAsUserId,
  };
}
